import React from 'react';
import {TextInput as PepsicoDsTextInput} from '@pepsico-ds/ui';
import PropTypes from 'prop-types';
import './TextInput.scss';

/**
 * TextInput component that renders a PepsicoDsTextInput with properties from props.
 *
 * @param {Object} props - The properties object.
 * @param {string|number} props.value - The value of the text input.
 * @param {Function} props.onChange - The function to handle input change events.
 * @param {string} props.label - The label text for the input.
 * @param {boolean} [props.required=false] - Optional boolean to indicate if the input is required.
 * @param {boolean} [props.disabled=false] - Optional boolean to disable the input.
 * @param {number} [props.maxLength] - Optional maximum length of the input value.
 * @param {string} [props.type='text'] - Optional type of the input.
 * @param {string} [props.error] - Optional error message to display.
 * @param {boolean} [props.isDark=false] - Optional boolean to indicate dark mode styling for the input.
 * @param {string} [props.placeholderText=''] - Optional placeholder text for the input.
 * @param {string} [props.helperText] - Optional helper text to display below the input.
 * @param {Function} [props.onBlur] - Optional function to handle input blur events.
 * @param {string} [props.className=''] - Optional additional CSS class names to apply to the input.
 * @param {string} [props.id=''] - Optional additional id to apply to the input.
 * @param {Function} props.handleKeyDown - Block decimal & non-integer keys.
 * @param {Function} [props.onPaste] - Optional function to handle paste events.
 * @param {boolean} [props.showLabelIcon=false] - Optional boolean to show label icon.
 * @returns {JSX.Element} The rendered PepsicoDsTextInput component.
 */

const TextInput = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  maxLength,
  type = 'text',
  error,
  isDark = false,
  placeholderText = '',
  helperText = '',
  onBlur,
  className = '',
  id,
  handleKeyDown,
  onPaste,
  showLabelIcon,
  infoIconTooltip,
  hideFeedbackIcon
}) => {
  const labelProp = infoIconTooltip
    ? {label, infoIconTooltip: {text: infoIconTooltip}}
    : label;

  const feedbackContent =
    hideFeedbackIcon && error ? (
      <div className='feedback error'>{error}</div>
    ) : undefined;

  return (
    <PepsicoDsTextInput
      data-testid={`text-input` + (id ? `_${id}` : '')}
      id={`text-input` + (id ? `_${id}` : '')}
      value={value}
      onUpdate={onChange}
      label={labelProp}
      required={required}
      disabled={disabled}
      maxLength={maxLength}
      type={type}
      error={error}
      isDark={isDark}
      placeholderText={placeholderText}
      helperText={helperText}
      onBlur={onBlur}
      showLabelIcon={showLabelIcon}
      feedbackContent={feedbackContent}
      className={`custom-text-input input-custom-wrapper ${className}`}
      onKeyDown={handleKeyDown}
      onPaste={onPaste}
      onWheel={(e) => e.currentTarget.blur()}
    />
  );
};

TextInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  type: PropTypes.string,
  error: PropTypes.string,
  isDark: PropTypes.bool,
  placeholderText: PropTypes.string,
  helperText: PropTypes.string,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
  handleKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  showLabelIcon: PropTypes.bool,
  infoIconTooltip: PropTypes.string,
  hideFeedbackIcon: PropTypes.bool
};

export default React.memo(TextInput);
